import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import SuperFetch from 'library/helpers/superFetch';
import { Fragment, useState } from 'react';
import { Notification } from 'zComponents/atoms';

import { Button, Divider, Flex, Modal, Space, Tag, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const UnassignInventoryAssets = ({
	children,
	devices = [],
	setDevices,
	open,
	setOpen,
	refetch,
	platform = 'Control',
	type = 'Locks'
}) => {
	const {
		token: { marginLG, marginXS, paddingLG, paddingMD, paddingXL, paddingXS }
	} = theme.useToken();

	const [loading, setLoading] = useState(false);

	const handleCancel = () => {
		setOpen(false);
		setDevices([]);
	};

	const handleUnassign = async () => {
		setLoading(true);

		let successCount = 0;
		let failedCount = 0;

		try {
			for (const device of devices) {
				try {
					await SuperFetch.delete(`/inventories/unassignAssets/${device.deviceID}`, {}, {}, device.orgID);
					successCount++;
				} catch (err) {
					failedCount++;
				}
			}

			if (failedCount > 0) {
				Notification('error', `Failed to unassign device${failedCount > 1 ? 's' : ''}. Please try again.`);
			}

			// clear selected devices only if at least one was successfully unassigned
			if (successCount > 0) {
				Notification('success', `Device${successCount > 1 ? 's' : ''} unassigned successfully.`);
				await refetch();
				handleCancel();
			}
		} catch (error) {
			Notification('error', 'Something went wrong. Please try again.');
		}

		setLoading(false);
	};

	const generateDeleteTitle = () =>
		devices.length > 1 ? `Unassign ${platform} ${type}s` : `Unassign ${platform} ${type}`;

	return (
		<Fragment>
			<Tooltip title={generateDeleteTitle()} placement='top' color='red'>
				{children ? (
					children
				) : (
					<Button
						icon={<DeleteOutlined />}
						onClick={() => setOpen(true)}
						disabled={devices.length === 0}
						danger
						type='primary'
					>
						Unassign
					</Button>
				)}
			</Tooltip>

			<Modal
				open={open}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				width={350}
				styles={{ content: { padding: `${paddingMD}px ${paddingXL}px` } }}
				footer={[
					<Flex align='center' justify='center' gap={12} key='footer' style={{ marginTop: marginLG }}>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button
							onClick={handleUnassign}
							loading={loading}
							disabled={loading || devices.length === 0}
							type='primary'
							danger
							block
						>
							Unassign
						</Button>
					</Flex>
				]}
			>
				<Flex vertical align='center' justify='center' gap={12} style={{ padding: `0 ${paddingLG}px` }}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space size={paddingXS} direction='vertical' align='center'>
							<Title level={5} style={{ margin: 0, lineHeight: '24px', textAlign: 'center' }}>
								{generateDeleteTitle()}
							</Title>
							<Text style={{ fontSize: 12, lineHeight: '24px', textAlign: 'center' }}>
								This action is permanent and cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS} style={{ marginBottom: marginXS }}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: 13, fontWeight: 200 }}>
									Selected {devices.length > 1 ? `${type}s` : type}
								</Text>
							</Divider>
							<Flex wrap='wrap' gap={6} justify='center'>
								{devices.length > 0 ? (
									devices.map((device) => (
										<Tag
											key={device.inventoryUID}
											closable
											onClose={() =>
												setDevices((prev) =>
													prev.filter((d) => d.inventoryUID !== device.inventoryUID)
												)
											}
											icon={<LockOutlined />}
											color='gold'
										>
											{device.name}
										</Tag>
									))
								) : (
									<Text type='secondary' style={{ fontSize: 13 }}>
										No devices selected
									</Text>
								)}
							</Flex>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default UnassignInventoryAssets;
